/** 4.30 Avatar: circle, photo cropped to fill, raised behind; ring variants; dashed and dimmed when offline. */
import { useState, type ReactElement } from "react";
import type { FrameId } from "@digu/shared";
import { isMock } from "../lib/api";
import { PictureFrame } from "./PictureFrame";
import { toneColor } from "../lib/tones";
export type AvatarRing = "none" | "edge" | "accent" | "thin";
/** People are circles; places (clubs) are rounded squares, so a club is never mistaken for a person. */
export type AvatarShape = "circle" | "square";
export interface AvatarProps { publicId: string | null; photoVersion?: number; name: string; size?: number; ring?: AvatarRing; offline?: boolean; className?: string; src?: string; shape?: AvatarShape; /** A win-milestone frame (D-057); it replaces the ring. */ frame?: FrameId | null; /** D-189: the initials colour (an AVATAR_TONES index); omitted, the one this person chose is looked up. */ tone?: number | null }
/**
 * The picture's URL, or null when there is none to fetch. Version 0 means the player never set a picture, so asking would
 * only 404 (audit #6); an unknown version (a payload without it) still asks.
 */
export function avatarSrc(publicId: string | null, photoVersion?: number): string | null {
  if (!publicId) return null;
  if (isMock()) { let h = 0; for (const ch of publicId) h = (h * 31 + ch.charCodeAt(0)) >>> 0; return `/art/v3/avatar-${(h % 4) + 1}.png`; }
  if (photoVersion === 0) return null;
  return `/api/avatars/${encodeURIComponent(publicId)}?v=${photoVersion ?? 0}`;
}
export function Avatar({ publicId, photoVersion, name, size = 48, ring = "none", offline = false, className = "", src, shape = "circle", frame = null, tone }: AvatarProps): ReactElement {
  const [failed, setFailed] = useState(false);
  const url = src ?? avatarSrc(publicId, photoVersion);
  const initials = name.split(/\s+/).map((p) => p[0] ?? "").join("").slice(0, 2).toUpperCase();
  const toned = shape === "circle" ? toneColor(publicId, tone) : null;
  const ringCls = frame ? "" : ring === "edge" ? "avatar--ring" : ring === "accent" ? "avatar--ring-accent" : ring === "thin" ? "avatar--ring-thin" : "";
  return (
    <span className={`avatar ${shape === "square" ? "avatar--square" : ""} ${ringCls} ${offline ? "avatar--offline" : ""} ${className}`} style={{ width: size, height: size, fontSize: size }} role="img" aria-label={name}>
      {url && !failed ? <img src={url} alt="" width={size} height={size} onError={() => setFailed(true)} /> : <span className={`avatar__fallback${toned ? " avatar__fallback--toned" : ""}`} style={toned ? { background: toned } : undefined} aria-hidden="true">{initials}</span>}
      {frame && shape === "circle" && <PictureFrame frame={frame} size={size} />}
    </span>
  );
}
